import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Button,
  Stack,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Email,
  Help,
  BugReport,
  Feedback,
  School,
  QuestionAnswer
} from '@mui/icons-material';
import SEO from '../utils/SEO';

/**
 * Contact/Support Component
 * 
 * Provides contact information and support options for TeachAssist users.
 */
const Contact: React.FC = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  const supportOptions = [
    {
      icon: <Email color="primary" />,
      title: "General Support",
      description: "Get help with account issues, technical problems, or general questions",
      action: "Email support@levelupteaching.ai",
      href: "mailto:support@levelupteaching.ai"
    },
    {
      icon: <BugReport color="primary" />,
      title: "Report a Bug",
      description: "Found something that's not working? Let us know so we can fix it",
      action: "Email bugs@levelupteaching.ai",
      href: "mailto:bugs@levelupteaching.ai"
    },
    {
      icon: <Feedback color="primary" />,
      title: "Feature Requests",
      description: "Have an idea for a new feature or improvement? We'd love to hear it",
      action: "Email feedback@levelupteaching.ai",
      href: "mailto:feedback@levelupteaching.ai"
    },
    {
      icon: <School color="primary" />,
      title: "Educational Partnerships",
      description: "Interested in partnering with us or bulk licensing for your school?",
      action: "Email partnerships@levelupteaching.ai",
      href: "mailto:partnerships@levelupteaching.ai"
    }
  ];

  return (
    <>
      <SEO
        title="Contact & Support"
        description="Get help with Level Up Teaching. Contact our support team for technical assistance, general inquiries, feature requests, or partnership opportunities."
        canonicalUrl="https://levelupteaching.ai/contact"
        keywords="contact support, teacher support, educational technology help, AI lesson planning support"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom color="primary">
            Contact & Support
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            We're here to help! Choose the best way to get in touch with our team.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Support Options */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {supportOptions.map((option, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => window.location.href = option.href}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ mt: 0.5 }}>
                      {option.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {option.description}
                      </Typography>
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                        {option.action}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Response Time Information */}
        <Box sx={{ mb: 4, bgcolor: 'grey.50', p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <QuestionAnswer color="primary" />
            <Typography variant="h6">
              Response Times
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>General Support:</strong> We typically respond within 24 hours during business days.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Bug Reports:</strong> Critical issues are addressed within 24 hours. Other bugs are typically resolved within 3-5 business days.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Feature Requests:</strong> We review all suggestions and will get back to you within a week if we need more information.
          </Typography>
        </Box>

        {/* Frequently Asked Questions */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Help color="primary" />
            <Typography variant="h5">
              Quick Help
            </Typography>
          </Stack>
          
          <Typography variant="h6" gutterBottom>
            Common Questions:
          </Typography>
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" paragraph>
              <strong>Q: How do I reset my password?</strong><br />
              A: Use the "Forgot Password" link on the login page, or contact support.
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Q: Can I import my existing Google Classroom data?</strong><br />
              A: Yes! Use the Google Classroom import feature in your student management section.
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Q: How do I delete my account and data?</strong><br />
              A: Go to Settings → Account → Delete Account, or contact support for assistance.
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Q: Is my student data secure?</strong><br />
              A: Absolutely. We comply with FERPA, COPPA, and other privacy regulations. Read our Privacy Policy for details.
            </Typography>
          </Box>
        </Box>

        {/* Business Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Business Information
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">
              <strong>Company:</strong> Level Up Teaching, LLC
            </Typography>
            <Typography variant="body2">
              <strong>Website:</strong> https://levelupteaching.ai
            </Typography>
            <Typography variant="body2">
              <strong>General Email:</strong> hello@levelupteaching.ai
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Footer Actions */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={handleBackClick}>
            Go Back
          </Button>
          <Button 
            variant="contained" 
            href="mailto:support@levelupteaching.ai"
          >
            Contact Support
          </Button>
        </Stack>
      </Paper>
    </Container>
    </>
  );
};

export default Contact;
